'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import OtpVerifyScreen from './OtpVerifyScreen';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useRouter } from 'next/navigation';

interface SignUpFormData {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { signInWithGoogle, sendOtp, verifyEmailOtp } = useAuth();
  const router = useRouter();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignUpFormData>();

  const passwordVal = watch('password');

  async function onSubmit(data: SignUpFormData) {
    setIsLoading(true);
    try {
      // Store fullName and password temporarily for use after OTP verification
      if (typeof window !== 'undefined') {
        localStorage.setItem('signup_fullName', data.fullName);
        localStorage.setItem('signup_password', data.password);
      }

      // Use the exact same OTP mechanism as login (signInWithOtp with shouldCreateUser=true)
      // This sends ONLY an OTP code — no magic link, no separate confirmation email
      await sendOtp(data.email, true);

      setRegisteredEmail(data.email);
      setOtpStep(true);
      toast.success('A verification code has been sent to your email.');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleGoogleSignUp() {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to sign up with Google. Please try again.');
      setIsGoogleLoading(false);
    }
  }

  async function handleOtpVerify(token: string) {
    // Verify OTP using the exact same mechanism as login
    await verifyEmailOtp(registeredEmail, token);

    // Set password and metadata now that the user is verified and signed in
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const password = typeof window !== 'undefined' ? localStorage.getItem('signup_password') || '' : '';
      const fullName = typeof window !== 'undefined' ? localStorage.getItem('signup_fullName') || '' : '';

      // Update password so user can also log in with email+password
      if (password) {
        await supabase.auth.updateUser({ password });
      }
      // Update display name
      if (fullName) {
        await supabase.auth.updateUser({ data: { full_name: fullName } });
      }
      if (password) localStorage.removeItem('signup_password');
      if (fullName) localStorage.removeItem('signup_fullName');
    } catch {
      // Non-blocking
    }

    // Send welcome email via Resend
    try {
      const fullName = typeof window !== 'undefined' ? localStorage.getItem('signup_fullName') || '' : '';
      await fetch('/api/email/send-welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail, fullName }),
      });
    } catch {
      // Non-blocking
    }

    toast.success('Account verified! Welcome to Monezite 🎉');
    router.push('/');
    router.refresh();
  }

  async function handleOtpResend() {
    // Resend OTP using the exact same mechanism as login
    await sendOtp(registeredEmail, false);
  }

  // OTP verification screen
  if (otpStep) {
    return (
      <OtpVerifyScreen
        email={registeredEmail}
        type="email"
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        onBack={() => setOtpStep(false)}
      />
    );
  }

  return (
    <div>
      <LoadingSpinner visible={isLoading || isGoogleLoading} label={isGoogleLoading ? 'Connecting with Google…' : 'Creating your account…'} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">Create your account</h1>
        <p className="text-sm text-muted-foreground">Join 15,000+ innovators on Monezite</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Full Name */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-name">
            Full name
          </label>
          <input
            id="signup-name"
            type="text"
            placeholder="e.g. Marshall Machina"
            className={`w-full px-4 py-3 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.fullName ? 'border-danger' : 'border-input'}`}
            {...register('fullName', {
              required: 'Full name is required',
              minLength: { value: 2, message: 'Name must be at least 2 characters' },
            })}
          />
          {errors.fullName && <p className="mt-1 text-xs text-danger font-medium">{errors.fullName.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-email">
            Work email
          </label>
          <input
            id="signup-email"
            type="email"
            placeholder="you@yourcompany.com"
            className={`w-full px-4 py-3 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.email ? 'border-danger' : 'border-input'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="mt-1 text-xs text-danger font-medium">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-password">
            Password
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters, at least one uppercase and one number</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              className={`w-full px-4 py-3 pr-11 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.password ? 'border-danger' : 'border-input'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*\d)/,
                  message: 'Include at least one uppercase letter and one number',
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-danger font-medium">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="signup-confirm">
            Confirm password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repeat your password"
              className={`w-full px-4 py-3 pr-11 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.confirmPassword ? 'border-danger' : 'border-input'}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (val) => val === passwordVal || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="mt-1 text-xs text-danger font-medium">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2.5">
          <input
            id="terms"
            type="checkbox"
            className="w-4 h-4 mt-0.5 rounded border-input accent-primary cursor-pointer shrink-0"
            {...register('agreeTerms', { required: 'You must agree to the terms to continue' })}
          />
          <label htmlFor="terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
            I agree to Monezite&apos;s{' '}
            <a href="#" className="text-primary font-semibold hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary font-semibold hover:underline">Privacy Policy</a>.
            I understand my data will be used to match problems, solutions, and investors.
          </label>
        </div>
        {errors.agreeTerms && <p className="text-xs text-danger font-medium">{errors.agreeTerms.message}</p>}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Creating your account...
            </>
          ) : (
            'Create Free Account'
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">or sign up with</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Social buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleGoogleSignUp}
          disabled={isGoogleLoading}
          className="flex items-center justify-center gap-2 py-2.5 border border-input rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isGoogleLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          Google
        </button>
        <button
          type="button"
          className="flex items-center justify-center gap-2 py-2.5 border border-input rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
          LinkedIn
        </button>
      </div>

      <p className="mt-5 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-primary font-semibold hover:underline"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}